import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Clock3, Copy, ExternalLink, Loader2, Mail, MessageSquareText, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Skeleton } from '@/shared/ui/skeleton';
import { useAuth } from '@/shared/lib/auth';
import { cn } from '@/shared/lib/cn';
import { isMasterEmail } from '@/shared/lib/navigation';
import { formatCount } from '@/features/marketing-shared';
import { createPixCharge, openChargesKey, type OpenCharges } from '../api/billing';
import type { PixCharge, QuotaChannel } from '../model/ledger';
import {
  RECHARGE_ERRORS,
  STAGE_COPY,
  chargeDueLabel,
  chargeStage,
  chargeTitle,
  chargeWhen,
  formatCents,
  parseRechargeAmount,
  pixAsset,
  rechargeErrorOf,
  rechargeErrorText,
  unitLabel,
  unitPriceLabel,
} from '../model/recharge-copy';
import { useChannelQuotas } from '../model/use-channel-quotas';
import { useOpenCharges } from '../model/use-open-charges';
import { PixQr } from './pix-qr';

export type RechargeTab = 'new' | 'open';

const ALL_CHANNELS: QuotaChannel[] = ['email', 'sms'];
const CHANNEL_LABEL: Record<QuotaChannel, string> = { email: 'E-mail', sms: 'SMS' };
const CHANNEL_ICON = { email: Mail, sms: MessageSquareText } as const;
/** Atalhos de quantidade por canal (o campo continua livre). */
const PRESETS: Record<QuotaChannel, number[]> = { email: [1_000, 5_000, 10_000], sms: [100, 500, 1_000] };

function balanceText(available: number | undefined): string {
  if (available == null) return '';
  return available > 0 ? `Saldo ${formatCount(available)}` : 'Sem saldo';
}

function TechnicalDetail({ text }: { text?: string }) {
  if (!text) return null;
  return <p className="text-[11px] text-muted-foreground">Detalhe técnico (só master): {text}</p>;
}

function ChannelCard({
  channel,
  active,
  available,
  loading,
  onSelect,
}: {
  channel: QuotaChannel;
  active: boolean;
  available?: number;
  loading: boolean;
  onSelect: () => void;
}) {
  const Icon = CHANNEL_ICON[channel];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      className={cn(
        'glass-thin flex min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-primary/60 bg-primary/[0.08]' : 'hover:bg-foreground/[0.03]',
      )}
    >
      <span
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-lg transition-colors',
          active ? 'bg-primary text-primary-foreground shadow-soft' : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{CHANNEL_LABEL[channel]}</span>
        {loading ? (
          <Skeleton className="mt-1 h-3 w-16 rounded" />
        ) : (
          <span className="block truncate text-xs text-muted-foreground">{balanceText(available)}</span>
        )}
      </span>
    </button>
  );
}

function PixPayment({ charge }: { charge: PixCharge }) {
  const asset = pixAsset(charge);
  const copy = async () => {
    if (!charge.copiaECola) return;
    try {
      await navigator.clipboard.writeText(charge.copiaECola);
      toast.success('Código PIX copiado.');
    } catch {
      toast.error('Não foi possível copiar o código.');
    }
  };

  return (
    <div className="space-y-3">
      {charge.copiaECola ? <PixQr value={charge.copiaECola} /> : null}
      {!charge.copiaECola && asset.image ? (
        <img src={asset.image} alt="QR Code PIX" className="mx-auto h-52 w-52 rounded-xl bg-white object-contain p-2" />
      ) : null}
      {charge.copiaECola ? (
        <Button type="button" className="w-full" onClick={() => void copy()}>
          <Copy />
          Copiar código PIX
        </Button>
      ) : null}
      {asset.link && !charge.copiaECola ? (
        <Button asChild className="w-full">
          <a href={asset.link} target="_blank" rel="noreferrer">
            <ExternalLink />
            Abrir PIX
          </a>
        </Button>
      ) : null}
    </div>
  );
}

function OpenChargeCard({
  charge,
  now,
  expanded,
  onToggle,
  master,
}: {
  charge: PixCharge;
  now: number;
  expanded: boolean;
  onToggle: () => void;
  master: boolean;
}) {
  const stage = chargeStage(charge, now);
  const copy = STAGE_COPY[stage];
  const Icon = CHANNEL_ICON[charge.channel];

  return (
    <div className="glass-thin rounded-xl p-3">
      <button
        type="button"
        className="flex w-full items-center gap-3 text-left focus-visible:outline-none"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{chargeTitle(charge)}</span>
          <span className="block text-xs text-muted-foreground">
            {formatCents(charge.amountCents)} · {chargeWhen(charge.createdAt)}
          </span>
        </span>
        <Badge variant={copy.tone} className="shrink-0">
          {stage === 'preparing' ? <Loader2 className="size-3 animate-spin" /> : null}
          {copy.label}
        </Badge>
      </button>
      {expanded ? (
        <div className="mt-3 space-y-3 border-t border-border/50 pt-3">
          {stage === 'awaiting' ? <PixPayment charge={charge} /> : null}
          <p className="text-center text-xs text-muted-foreground">
            {copy.hint}
            {stage === 'awaiting' ? ` ${chargeDueLabel(charge.createdAt)}.` : null}
          </p>
          {master && (stage === 'unavailable' || stage === 'failed') ? <TechnicalDetail text={charge.message} /> : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Recarga avulsa de e-mail/SMS por PIX. A aba "Em andamento" lê as cobranças
 * da loja no servidor: fechar o diálogo ou recarregar a página não perde o PIX.
 */
export function RechargeDialog({
  open,
  onOpenChange,
  passbookId,
  channel,
  channels = ALL_CHANNELS,
  initialTab,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  passbookId: string;
  /** Canal já escolhido ao abrir. */
  channel?: QuotaChannel;
  /** Canais que a loja pode recarregar; com um só, o seletor some. */
  channels?: QuotaChannel[];
  initialTab?: RechargeTab;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const master = isMasterEmail(user?.email);
  const { charges, pending, prices, isSuccess } = useOpenCharges(passbookId, { active: open });
  const balances = useChannelQuotas(passbookId);
  const [tab, setTab] = useState<RechargeTab>('new');
  const [selected, setSelected] = useState<QuotaChannel>(channel ?? channels[0] ?? 'email');
  const [amount, setAmount] = useState('1000');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; detail?: string } | null>(null);
  const [paidHere, setPaidHere] = useState<PixCharge[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const wasOpen = useRef(false);
  const channelsKey = channels.join(',');

  useEffect(() => {
    if (open && !wasOpen.current) {
      const first = channel ?? ((channelsKey.split(',')[0] || 'email') as QuotaChannel);
      setSelected(first);
      setAmount(String(PRESETS[first][0]));
      setError(null);
      setBusy(false);
      setPaidHere([]);
      setExpandedId(null);
      setTab(initialTab ?? 'new');
    }
    wasOpen.current = open;
  }, [open, channel, channelsKey, initialTab]);

  // "Gerando PIX" vira "indisponível" com o tempo, mesmo sem resposta nova.
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, [open]);

  // Pago enquanto o diálogo está aberto continua na lista até fechar.
  useEffect(() => {
    if (!open) return;
    const paid = charges.filter((charge) => charge.status === 'paid');
    if (paid.length) setPaidHere((prev) => [...prev.filter((p) => !paid.some((c) => c.id === p.id)), ...paid]);
  }, [charges, open]);

  const listed = useMemo(() => {
    const byId = new Map<string, PixCharge>();
    for (const charge of [...paidHere, ...pending]) byId.set(charge.id, charge);
    return [...byId.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [paidHere, pending]);

  const showTabs = listed.length > 0;
  const current: RechargeTab = showTabs ? tab : 'new';
  const openId = expandedId ?? listed[0]?.id ?? null;
  const parsed = parseRechargeAmount(amount);
  const unitCents = prices[selected];
  // Preço lido do servidor e zerado = recarga não configurada: avisa antes do clique.
  const priceMissing = isSuccess && unitCents <= 0;
  const available = balances.data?.[selected].available;
  const unit = unitLabel(selected, 2);

  const choose = (next: QuotaChannel) => {
    setSelected(next);
    setAmount(String(PRESETS[next][0]));
    setError(null);
  };

  const generate = async () => {
    if ('error' in parsed) {
      setError({ text: parsed.error });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await createPixCharge({ passbookId, channel: selected, slots: parsed.slots });
      const charge = response.charge;
      if (!charge || charge.status === 'failed' || response.status === 'unavailable') {
        setError({ text: rechargeErrorText({ code: response.code }), detail: response.message });
        return;
      }
      queryClient.setQueryData<OpenCharges>(openChargesKey(passbookId), (prev) => ({
        prices: prev?.prices ?? prices,
        charges: [charge, ...(prev?.charges ?? []).filter((item) => item.id !== charge.id)],
      }));
      void queryClient.invalidateQueries({ queryKey: openChargesKey(passbookId) });
      setExpandedId(charge.id);
      setTab('open');
    } catch (err) {
      const info = rechargeErrorOf(err);
      setError({ text: rechargeErrorText(info), detail: info.detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Recarregar</DialogTitle>
          <DialogDescription>Compre saldo por PIX. Ele entra assim que o pagamento é confirmado.</DialogDescription>
        </DialogHeader>

        {showTabs ? (
          <SegmentedControl
            fill
            size="sm"
            value={current}
            onChange={setTab}
            options={[
              { value: 'new', label: 'Nova recarga', icon: Plus },
              {
                value: 'open',
                icon: Clock3,
                label: (
                  <>
                    Em andamento
                    {pending.length ? <span className="ml-1 tabular-nums opacity-80">· {pending.length}</span> : null}
                  </>
                ),
              },
            ]}
          />
        ) : null}

        {current === 'new' ? (
          <div className="space-y-4">
            {channels.length > 1 ? (
              <div role="radiogroup" aria-label="Canal" className="grid grid-cols-2 gap-2">
                {channels.map((item) => (
                  <ChannelCard
                    key={item}
                    channel={item}
                    active={selected === item}
                    available={balances.data?.[item].available}
                    loading={balances.isPending}
                    onSelect={() => choose(item)}
                  />
                ))}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="recharge-amount">Quantidade</Label>
              <div className="relative">
                <Input
                  id="recharge-amount"
                  inputMode="numeric"
                  autoComplete="off"
                  className="pr-20 tabular-nums"
                  value={amount}
                  onChange={(event) => {
                    setAmount(event.target.value.replace(/[^\d]/g, ''));
                    setError(null);
                  }}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-sm text-muted-foreground">
                  {unit}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESETS[selected].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => {
                      setAmount(String(preset));
                      setError(null);
                    }}
                    className={cn(
                      'glass-thin rounded-full px-3 py-1 text-xs font-medium tabular-nums transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      amount === String(preset)
                        ? 'border-primary/60 bg-primary/[0.08] text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {formatCount(preset)}
                  </button>
                ))}
              </div>
            </div>

            {unitCents > 0 ? (
              <div className="glass-thin space-y-1.5 rounded-xl px-3.5 py-3 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-muted-foreground">
                    {'slots' in parsed
                      ? `${formatCount(parsed.slots)} × ${formatCents(unitCents)}`
                      : unitPriceLabel(selected, unitCents)}
                  </span>
                  <span className="text-lg font-semibold tabular-nums">
                    {'slots' in parsed ? formatCents(parsed.slots * unitCents) : '—'}
                  </span>
                </div>
                {available != null && 'slots' in parsed ? (
                  <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
                    <span>Saldo depois do pagamento</span>
                    <span className="tabular-nums">
                      {formatCount(Math.max(0, available) + parsed.slots)} {unit}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {priceMissing && !error ? (
              <div role="status" className="space-y-1">
                <p className="text-sm text-muted-foreground">{RECHARGE_ERRORS.unavailable}</p>
                {master ? (
                  <TechnicalDetail text={`Sem preço para ${CHANNEL_LABEL[selected]} (BLING_EMAIL_SLOT_CENTS / BLING_SMS_SLOT_CENTS).`} />
                ) : null}
              </div>
            ) : null}
            {error ? (
              <div role="alert" className="space-y-1">
                <p className="text-sm text-destructive">{error.text}</p>
                {master ? <TechnicalDetail text={error.detail} /> : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-2.5" aria-live="polite">
            {listed.map((charge) => (
              <OpenChargeCard
                key={charge.id}
                charge={charge}
                now={now}
                expanded={charge.id === openId}
                onToggle={() => setExpandedId(charge.id === openId ? '' : charge.id)}
                master={master}
              />
            ))}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {current === 'new' ? (
            <Button type="button" onClick={() => void generate()} loading={busy} disabled={priceMissing}>
              Gerar PIX
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
